import { Injectable, inject } from '@angular/core';
import Config from '../utils/config.service';

export type AppLanguageOption = {
	code: string;
	icon: string;
	locale: string;
	nativeName: string;
	shortLabel: string;
};

@Injectable({ providedIn: 'root' })
export default class AppLanguageService {
	private config: Config = inject(Config);

	getLanguagesByOrigin(origin?: string): Array<AppLanguageOption> {
		const settings = this.config.getSettings();
		const locales = settings.DeviceLanguages?.[origin || ''] || [];
		return locales.map((locale) => this.getLanguage(locale));
	}

	getLanguage(locale?: string): AppLanguageOption {
		const settings = this.config.getSettings();
		const normalizedLocale = this.normalizeLocale(locale);
		const configEntry = settings.Languages?.[normalizedLocale] || {};
		const overrideEntry = this.config.getLanguageOverrides()?.[normalizedLocale] || {};
		const code = overrideEntry.code || normalizedLocale.split('_')[0].toLowerCase();

		return {
			code,
			icon: overrideEntry.icon || configEntry.icon || code,
			locale: normalizedLocale,
			nativeName: overrideEntry.nativeName || configEntry.label || normalizedLocale,
			shortLabel: overrideEntry.shortLabel || configEntry.label || normalizedLocale
		};
	}

	getDefaultLocale(): string {
		return this.config.getSettings().DEFAULT_LANG || 'en_GB';
	}

	normalizeLocale(locale?: string): string {
		const defaultLocale = this.getDefaultLocale();
		const supportedLocales = Array.from(new Set([
			...Object.keys(this.config.getSettings().Languages || {}),
			...Object.keys(this.config.getLanguageOverrides() || {})
		]));

		if (!locale) {
			return defaultLocale;
		}
		if (supportedLocales.indexOf(locale) > -1) {
			return locale;
		}
		if (locale.indexOf('_') === -1 && locale.length === 2) {
			const matchedLocale = supportedLocales.find((supportedLocale) => {
				return supportedLocale.toLowerCase().indexOf(locale.toLowerCase() + '_') === 0;
			});
			if (matchedLocale) {
				return matchedLocale;
			}
		}
		return defaultLocale;
	}
}
